import type { ShopifyGlobal } from "@shopify/app-bridge-types";

export interface ApiClientOptions {
  app?: ShopifyGlobal;
  baseUrl?: string;
}

export interface ConnectionStatus {
  connected: boolean;
  shopDomain?: string;
  embedBaseUrl?: string;
  flipbookerAppUrl?: string;
  workspaceName?: string;
  /** The generated signup name may be replaced once. */
  canRenameWorkspace?: boolean;
  /** Billable plan name; a `free` workspace can do nothing until it subscribes. */
  workspacePlan?: string;
  lastValidated?: string;
  authMethod?: "oauth" | "api_key";
}

export interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  availableForSale: boolean;
  descriptionHtml?: string;
  onlineStoreUrl?: string;
  featuredImage?: { url: string; altText?: string };
  priceRange?: { minVariantPrice: { amount: string; currencyCode: string } };
}

export interface ProductsPage {
  products: ShopifyProduct[];
  unavailableProductCount: number;
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
}

export interface CatalogCreateResult {
  ok: boolean;
  batchId: string | null;
  book: {
    random_id: string;
    name?: string;
    status?: string;
    page_count?: number;
    embed_code?: string;
    batch_id?: string | null;
  };
}

export interface CatalogCreatePayload {
  title: string;
  description?: string;
  /** Optional — when omitted the platform renders its default product layout. */
  template_id?: number;
  productIds: string[];
}

export interface Template {
  id: number;
  name: string;
  thumbnails?: string[];
  is_default?: boolean;
  type?: string;
}

export type BatchStatusValue =
  | "processing"
  | "queued"
  | "complete"
  | "partial_failure"
  | "failed"
  | "cancelled";

export interface BatchStatus {
  id: string;
  status: BatchStatusValue | string;
  progress?: {
    total: number;
    completed: number;
    pending: number;
    failed: number;
  };
  books?: unknown[];
  finished_at?: string | null;
}

export function createApiClient(options: ApiClientOptions) {
  const baseUrl = options.baseUrl ?? "";

  async function authenticatedFetch(
    input: RequestInfo,
    init: RequestInit = {},
  ) {
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");

    if (options.app) {
      const token = await options.app.idToken();
      headers.set("Authorization", `Bearer ${token}`);
    }

    return fetch(`${baseUrl}${input}`, {
      ...init,
      headers,
      credentials: "include",
    });
  }

  return {
    async getConnectionStatus(): Promise<ConnectionStatus> {
      const response = await authenticatedFetch("/api/connection-status");
      if (!response.ok) {
        throw new Error("Failed to fetch status");
      }
      return response.json();
    },

    async getFlipbookerOAuthUrl(): Promise<{ authorizeUrl: string }> {
      const response = await authenticatedFetch("/api/flipbooker/oauth-url");
      if (!response.ok) {
        throw new Error("Failed to start Flipbooker OAuth");
      }
      return response.json();
    },

    async connectFlipbooker(apiKey: string) {
      const response = await authenticatedFetch("/api/connect-flipbooker", {
        method: "POST",
        body: JSON.stringify({ apiKey }),
      });
      if (!response.ok) {
        throw new Error("Failed to connect workspace");
      }
      return response.json();
    },

    async disconnectFlipbooker() {
      const response = await authenticatedFetch("/api/disconnect-flipbooker", {
        method: "POST",
      });
      if (!response.ok) {
        throw new Error("Failed to disconnect workspace");
      }
      return response.json();
    },

    async renameWorkspace(
      workspaceName: string,
    ): Promise<{ workspaceName: string; canRenameWorkspace: false }> {
      const response = await authenticatedFetch("/api/workspace-name", {
        method: "POST",
        body: JSON.stringify({ workspaceName }),
      });
      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || "Failed to update workspace name");
      }
      return response.json();
    },

    async createCatalog(
      payload: CatalogCreatePayload,
    ): Promise<CatalogCreateResult> {
      const response = await authenticatedFetch("/api/catalogs", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || "Failed to create catalog");
      }
      return response.json();
    },

    async listTemplates(): Promise<Template[]> {
      const response = await authenticatedFetch("/api/templates");
      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || "Failed to load templates");
      }
      const body = (await response.json()) as {
        templates?: Template[];
        data?: Template[];
      };
      return body.templates ?? body.data ?? [];
    },

    async getBatchStatus(batchId: string): Promise<BatchStatus> {
      const response = await authenticatedFetch(
        `/api/batches/${encodeURIComponent(batchId)}`,
      );
      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || "Failed to fetch catalog status");
      }
      return response.json();
    },

    async getProducts(
      params: { query?: string; cursor?: string } = {},
    ): Promise<ProductsPage> {
      const qs = new URLSearchParams();
      if (params.query) qs.set("query", params.query);
      if (params.cursor) qs.set("cursor", params.cursor);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const response = await authenticatedFetch(
        `/api/shopify/products${suffix}`,
      );
      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || "Failed to load products");
      }
      return response.json();
    },

    async listBooks(cursor?: string) {
      const query = cursor ? `?cursor=${cursor}` : "";
      const response = await authenticatedFetch(`/api/books${query}`);
      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || "Failed to load books");
      }
      return response.json();
    },
  };
}
